import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button.tsx';
import { Panel } from '../components/ui/primitives.tsx';
import { acknowledgedDeclaration, currentIntakeStatus, enqueueIntakeVerification, intakeActor, ownsJob, operatorView, HelperError,
  pairedHelper, pendingDeclarations, sendPreparedJob, reconnectTakenOverJob, retireDeclaration, confirmDeclaration, type CardFolder, type IntakeActor, type IntakeDeclaration,
  type IntakeJob, type IntakeStatus, type PairedHelper, type HelperInfo } from '../lib/intake.ts';
import { intakeCopy, codeSentences } from './intake-copy.ts';
import { ApiError, sessionEnded, sessionIsEnding } from '../lib/api.ts';

/** The helper's one reason for "the signed upload links ran out": Continue fetches a new plan and sends only missing parts. */
const linksExpired = (job: IntakeJob) => job.phase === 'paused' && job.reason === 'refresh_plan_required';
const AUTO_CONTINUE_LIMIT = 24, AUTO_CONTINUE_DELAY = 5000;

export function IntakeUpload({ changed, cardLabels = {}, onResumeDeclaration }: { changed: number; cardLabels?: Record<string, string>; onResumeDeclaration?: (receipt: IntakeDeclaration) => void }) {
  const { i18n } = useTranslation(), c = intakeCopy(i18n.language);
  const [token, setToken] = useState(''), [helper, setHelper] = useState<PairedHelper | null>(null);
  const [actor, setActor] = useState<IntakeActor | null>(null), [pending, setPending] = useState<IntakeDeclaration[]>([]);
  const [folders, setFolders] = useState<CardFolder[]>([]), [jobs, setJobs] = useState<IntakeJob[]>([]);
  const [selected, setSelected] = useState(''), [folder, setFolder] = useState(''), [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [cloud, setCloud] = useState<Record<string, { status: IntakeStatus | null; fetch: 'idle' | 'ok' | 'error'; at: number | null }>>({}), [retryReason, setRetryReason] = useState('');
  const [cloudRefresh, setCloudRefresh] = useState(0);
  const [retireReason, setRetireReason] = useState('');
  const [info, setInfo] = useState<HelperInfo | null>(null);
  const epoch = useRef(0), stopped = useRef(false);
  useEffect(() => { stopped.current = false; return () => { stopped.current = true; epoch.current++; }; }, []);
  const attempt = async (action: () => Promise<void>) => {
    if (busy || sessionIsEnding()) return;
    setBusy(true); setError(null);
    try { await action(); } catch (e) {
      if (e instanceof ApiError && e.status === 401) { sessionEnded(); return; }
      if (!stopped.current) {
        const code = e instanceof ApiError ? e.constraint ?? e.message : e instanceof Error ? e.message : 'intake_failed';
        setError(code); setLastError(code);
        if (e instanceof HelperError && [401,403].includes(e.status)) { epoch.current++; setHelper(null); setToken(''); }
      }
    }
    finally { if (!stopped.current) setBusy(false); }
  };
  useEffect(() => {
    const controller = new AbortController();
    void intakeActor(controller.signal).then(current => {
      if (!controller.signal.aborted) { setActor(current); setPending(pendingDeclarations(current)); }
    }).catch(e => {
      if (e instanceof ApiError && e.status === 401) { sessionEnded(); return; }
      if (!controller.signal.aborted) { setError((e as Error).message); setLastError((e as Error).message); }
    });
    return () => controller.abort();
  }, [changed]);
  useEffect(() => {
    const committed = pending.filter(d => d.phase === 'committed');
    setSelected(current => committed.length === 1 ? committed[0]!.sessionId : committed.some(d => d.sessionId === current) ? current : '');
  }, [pending]);
  useEffect(() => {
    if (!helper || !actor) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (sessionIsEnding() || controller.signal.aborted) return;
      try {
        const currentActor = await intakeActor(controller.signal);
        if (sessionIsEnding() || controller.signal.aborted) return;
        if (JSON.stringify(currentActor) !== JSON.stringify(actor)) throw new Error('operator_changed');
        const rows = await helper.request<IntakeJob[]>('/v1/jobs', undefined, controller.signal);
        if (!controller.signal.aborted) setJobs(rows);
        if (!controller.signal.aborted) timer = setTimeout(poll, rows.some(row => ['copying','measuring','uploading'].includes(row.phase)) ? 2000 : 15000);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) { sessionEnded(); return; }
        if (!controller.signal.aborted) {
          setError((e as Error).message); setLastError((e as Error).message);
          if ((e instanceof HelperError && [401,403].includes(e.status)) || (e instanceof ApiError && e.status === 403) || (e as Error).message === 'operator_changed') {
            epoch.current++; setHelper(null); setToken('');
          } else timer = setTimeout(poll, 10000);
        }
      }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [helper, actor]);
  const cloudTargets = jobs.filter(job => job.ingest_id && job.target_batch_id && ['awaiting_verification','completed','failed','paused'].includes(job.phase));
  const cloudKey = cloudTargets.map(j => `${j.id}:${j.phase}:${j.ingest_id}`).join('|');
  useEffect(() => {
    if (!helper || !actor || !cloudTargets.length) return;
    const controller = new AbortController();
    const generation = epoch.current;
    const settled = new Set<string>();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      let again = false;
      for (const job of cloudTargets) {
        if (sessionIsEnding() || stopped.current || generation !== epoch.current || controller.signal.aborted) return;
        if (settled.has(job.id) || job.centre_id !== actor.upload_centre_id || job.device_id !== actor.upload_device_id || controller.signal.aborted) continue;
        try {
          let retryReceipt = false;
          const result = await currentIntakeStatus(job, controller.signal);
          if (!sessionIsEnding() && !controller.signal.aborted) setCloud(current => ({ ...current, [job.id]: { status: result, fetch: 'ok', at: Date.now() } }));
          if (!sessionIsEnding() && !stopped.current && generation === epoch.current && !controller.signal.aborted && result.state === 'verified' && job.phase !== 'completed' &&
            ownsJob(actor, job) && result.current_operator_owns_job) {
            // A queued 202 is not the final receipt. Persist the fresh cloud result
            // so offline local-state archival can recognize a completed transfer.
            try {
              const saved = await helper.request<IntakeJob>(`/v1/jobs/${job.id}/receipt`, result, controller.signal);
              if (saved.id !== job.id || saved.ingest_id !== job.ingest_id) throw new Error('job_identity_mismatch');
              if (!controller.signal.aborted) setJobs(rows => rows.map(row => row.id === job.id ? saved : row));
            } catch (e) {
              if (e instanceof HelperError && [401,403].includes(e.status)) throw e;
              if (!controller.signal.aborted) { setLastError((e as Error).message); retryReceipt = true; }
            }
          }
          if (retryReceipt || ['queued','verifying','retry_scheduled'].includes(result.state)) again = true;
          else settled.add(job.id);
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) { sessionEnded(); return; }
          if (e instanceof HelperError && [401,403].includes(e.status)) { epoch.current++; setHelper(null); setToken(''); setError(e.message); setLastError(e.message); return; }
          if (!controller.signal.aborted) {
            setCloud(current => ({ ...current, [job.id]: { status: current[job.id]?.status ?? null, fetch: 'error', at: current[job.id]?.at ?? null } }));
            setLastError(e instanceof Error ? e.message : 'intake_failed'); again = true;
          }
        }
      }
      if (again && !sessionIsEnding() && !controller.signal.aborted) timer = setTimeout(poll, 5000);
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
    // Stable identity/phase signature avoids restarting the cloud timer on every local progress poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helper, actor, cloudKey, cloudRefresh]);
  const connect = () => attempt(async () => {
    const generation = ++epoch.current, current = await intakeActor(), local = pairedHelper(token.trim(), current);
    const helperInfo = await local.connect();
    const declarations = pendingDeclarations(current), rows = await local.request<IntakeJob[]>('/v1/jobs');
    if (sessionIsEnding() || stopped.current || generation !== epoch.current) return;
    setActor(current); setPending(declarations); setJobs(rows); setHelper(local); setInfo(helperInfo); setToken('');
  });
  const refresh = () => attempt(async () => {
    const rows = await helper!.request<CardFolder[]>('/v1/sessions');
    setFolders(rows); setFolder(''); setConfirmed(false);
  });
  const queue = () => attempt(async () => {
    const generation = epoch.current;
    const declaration = pending.find(d => d.sessionId === selected);
    if (!declaration || declaration.phase !== 'committed' || !confirmed || !folder || !actor || !helper) throw new Error('declaration_required');
    const current = await intakeActor();
    if (JSON.stringify(current) !== JSON.stringify(actor)) throw new Error('operator_changed');
    if (sessionIsEnding() || stopped.current || generation !== epoch.current) return;
    // Stable job ID = declaration ID; a lost helper response cannot mint another local transfer.
    const job = await helper.request<IntakeJob>('/v1/jobs', { id: declaration.sessionId, source_id: folder,
      handover_id: declaration.handoverId, session_id: declaration.sessionId, batch_id: declaration.batchId,
      operator_id: actor.operator_id, centre_id: actor.upload_centre_id, device_id: actor.upload_device_id });
    if (job.id !== declaration.sessionId || job.session_id !== declaration.sessionId || !ownsJob(actor, job)) throw new Error('job_identity_mismatch');
    acknowledgedDeclaration(declaration); setPending(pendingDeclarations(actor));
    setJobs(await helper.request<IntakeJob[]>('/v1/jobs')); setConfirmed(false);
    if (sessionIsEnding() || stopped.current || generation !== epoch.current) return;
    await helper.request(`/v1/jobs/${job.id}/prepare`, {});
  });
  const resume = (job: IntakeJob) => attempt(async () => {
    const generation = epoch.current;
    const live = () => !sessionIsEnding() && !stopped.current && generation === epoch.current;
    const current = await intakeActor();
    if (!ownsJob(current, job)) throw new Error('operator_changed');
    if (!live()) return;
    if (job.record) await sendPreparedJob(job, helper!, undefined, live);
    else await helper!.request(`/v1/jobs/${job.id}/prepare`, {});
    if (!live()) return;
    setJobs(await helper!.request<IntakeJob[]>('/v1/jobs'));
  });
  // Expired links only: press Continue for the operator, at most 24 times per job while this page is open.
  // Any other stop reason, an operator Pause (reason 'paused') or an ended session still waits for a person.
  const [renewed, setRenewed] = useState<Record<string, number>>({});
  const autoPending = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const autoContinue = useRef((_id: string) => {});
  autoContinue.current = jobId => { // reassigned each render, so the timer acts on current state
    const job = jobs.find(row => row.id === jobId);
    if (busy || !helper || !actor || !job || !linksExpired(job) || !ownsJob(actor, job) || sessionIsEnding() ||
      (renewed[jobId] ?? 0) >= AUTO_CONTINUE_LIMIT || jobs.some(row => ['copying','measuring','uploading'].includes(row.phase))) {
      autoPending.current.delete(jobId); return; // the next job poll schedules it again if still expired
    }
    setRenewed(counts => ({ ...counts, [jobId]: (counts[jobId] ?? 0) + 1 }));
    void resume(job).finally(() => autoPending.current.delete(jobId));
  };
  useEffect(() => {
    if (!helper || !actor) return;
    for (const job of jobs) if (linksExpired(job) && ownsJob(actor, job) && (renewed[job.id] ?? 0) < AUTO_CONTINUE_LIMIT && !autoPending.current.has(job.id))
      autoPending.current.set(job.id, setTimeout(() => autoContinue.current(job.id), AUTO_CONTINUE_DELAY));
  }, [jobs, helper, actor, renewed]);
  useEffect(() => { const timers = autoPending.current; return () => { timers.forEach(clearTimeout); timers.clear(); }; }, []);
  const verify = (job: IntakeJob, retry: boolean) => attempt(async () => {
    const generation = epoch.current;
    const live = () => !sessionIsEnding() && !stopped.current && generation === epoch.current;
    const current = await intakeActor();
    if (!ownsJob(current, job)) throw new Error('operator_changed');
    if (!live()) return;
    const result = await enqueueIntakeVerification(job, retry ? { retry: true, retry_reason: retryReason.trim() } : undefined, live);
    if (!live() || !result) return;
    setCloud(values => ({ ...values, [job.id]: { status: result, fetch: 'ok', at: Date.now() } }));
    setCloudRefresh(value => value + 1);
    // A phase update causes a fresh status read, and no helper-cached success is used as verification.
    await helper!.request(`/v1/jobs/${job.id}/receipt`, result);
    if (!live()) return;
    setJobs(await helper!.request<IntakeJob[]>('/v1/jobs'));
  });
  const takeover = (job: IntakeJob) => attempt(async () => {
    const generation = epoch.current;
    const live = () => !sessionIsEnding() && !stopped.current && generation === epoch.current;
    const current = await intakeActor(), status = await currentIntakeStatus(job);
    if (!status.can_take_over || !retryReason.trim()) throw new Error('takeover_refused');
    if (!live()) return;
    const result = await enqueueIntakeVerification(job, { takeover_reason: retryReason.trim() }, live);
    if (!live() || !result) return;
    setCloud(values => ({ ...values, [job.id]: { status: result, fetch: 'ok', at: Date.now() } })); setCloudRefresh(value => value + 1);
    if (!result.current_operator_owns_job) throw new Error('takeover_refused');
    await helper!.request(`/v1/jobs/${job.id}/receipt`, { ...result, acting_operator_id: current.operator_id, takeover_reason: retryReason.trim() });
    if (!live()) return;
    setJobs(await helper!.request<IntakeJob[]>('/v1/jobs')); setCloudRefresh(value => value + 1);
  });
  const label = (phase: string) => c[phase as keyof typeof c] ?? c.failed;
  return <Panel className="mt-8 space-y-4 p-5">
    <h2 className="text-xl font-semibold">{c.title}</h2><p>{c.intro}</p>
    {error && <p role="alert">{c[codeSentences[error] ?? 'error']}</p>}
    {pending.filter(d => d.phase !== 'committed').map(d => <div key={d.sessionId} role="status"><p>{d.phase === 'committing' ? c.committing : c.refused}</p><p>{cardLabels[d.sessionId] ?? `${c.savedDeclaration} · ${d.sessionId.slice(-6)}`}</p>
      <Button disabled={busy || !onResumeDeclaration} onClick={() => onResumeDeclaration?.(d)}>{c.resumeDeclaration}</Button>
      {d.phase === 'committing' && <Button disabled={busy} variant="outline" onClick={() => void attempt(async () => {
        const current = await confirmDeclaration(d); setPending(pendingDeclarations(current));
      })}>{c.checkDeclaration}</Button>}</div>)}
    {pending.some(d => d.phase !== 'committing') && <div className="space-y-2"><p>{c.retireHint}</p>
      <label>{c.retireReason}<input maxLength={500} value={retireReason} onChange={e => setRetireReason(e.target.value)} className="block w-full border p-2" /></label>
      {pending.filter(d => d.phase !== 'committing').map(d => <div key={d.sessionId}><p>{cardLabels[d.sessionId] ?? `${c.savedDeclaration} · ${d.sessionId.slice(-6)}`}</p>
        <Button variant="outline" disabled={busy || !retireReason.trim()} onClick={() => void attempt(async () => {
          const current = await intakeActor();
          if (current.operator_id !== d.operatorId || current.upload_centre_id !== d.centreId || current.upload_device_id !== d.deviceId) throw new Error('operator_changed');
          retireDeclaration(d, retireReason); setPending(pendingDeclarations(current));
        })}>{c.retire}</Button></div>)}</div>}
    {!helper ? <div className="grid gap-3"><p>{c.help}</p><label>{c.token}<input type="password" autoComplete="off" value={token}
      onChange={e => setToken(e.target.value)} className="block w-full rounded border p-2" /></label>
      <Button disabled={busy || !token.trim()} onClick={() => void connect()}>{c.connect}</Button><p>{c.unpaired}</p></div>
      : <><Button variant="outline" disabled={busy} onClick={() => { epoch.current++; setHelper(null); setActor(null); setJobs([]); setCloud({}); setToken(''); }}>{c.disconnect}</Button>
      <h3 className="font-semibold">{c.pending}</h3>
      {!pending.length ? <p>{c.noDeclarations}</p> : <div className="grid gap-3">
        <label>{c.declaration}<select value={selected} onChange={e => { setSelected(e.target.value); setConfirmed(false); }} className="block w-full border p-2">
          <option value="">—</option>{pending.filter(d => d.phase === 'committed').map(d => <option key={d.sessionId} value={d.sessionId}>{cardLabels[d.sessionId] ?? `${c.savedDeclaration} · ${d.sessionId.slice(-6)}`}</option>)}</select></label>
        <Button variant="outline" disabled={busy} onClick={() => void refresh()}>{c.refresh}</Button>
        <label>{c.folder}<select value={folder} onChange={e => { setFolder(e.target.value); setConfirmed(false); }} className="block w-full border p-2">
          <option value="">{c.choose}</option>{folders.map(f => <option key={f.id} value={f.id} disabled={!!f.reason}>{f.basename} — {f.reason ? c[codeSentences[f.reason] ?? 'error'] : `${f.files} ${c.files}, ${new Intl.NumberFormat(i18n.language, { style: 'unit', unit: (f.bytes ?? 0) >= 1e9 ? 'gigabyte' : 'megabyte', maximumFractionDigits: 1 }).format((f.bytes ?? 0) / ((f.bytes ?? 0) >= 1e9 ? 1e9 : 1e6))}`}</option>)}</select></label>
        <label><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} /> {c.confirm}</label>
        <Button disabled={busy || !selected || !folder || !confirmed} onClick={() => void queue()}>{c.queue}</Button>
      </div>}
      <h3 className="font-semibold">{c.jobs}</h3>{!jobs.length && <p>{c.noJobs}</p>}
      {jobs.map(job => {
        const observation = cloud[job.id] ?? { status: null, fetch: 'idle', at: null };
        const view = operatorView({ job, actor, cloud: observation });
        const message = view.problem ?? view.note ?? 'error';
        const state = view.kind === 'ready' ? (job.phase === 'queued' ? c.readyCopy : view.primary === 'verify' ? c.readyCheck : c.awaiting_plan)
          : view.kind === 'copying' ? label(job.phase) : view.kind === 'attention' ? c.failed : c[view.kind];
        return <article key={job.id} className="space-y-2 border-t py-4"><h4 className="font-semibold">{job.basename}</h4>
          <p className="text-xl font-semibold" role={view.kind === 'attention' ? 'alert' : 'status'}>{state}</p>
          {view.progress && <progress aria-label={state} {...view.progress} />}
          {(view.primary === 'takeover' || view.secondary === 'retry') && <label>{c.reason}<input className="block border p-2" maxLength={500} value={retryReason} onChange={e => setRetryReason(e.target.value)} /></label>}
          <div className="flex flex-wrap gap-2">{[view.primary, view.secondary].map((action, index) => action && <Button key={action}
            variant={index === 0 ? 'primary' : 'outline'} disabled={busy || ((action === 'retry' || action === 'takeover') && !retryReason.trim())}
            onClick={() => {
              if (action === 'continue') void resume(job);
              else if (action === 'verify' || action === 'retry') void verify(job, action === 'retry');
              else if (action === 'takeover') void takeover(job);
              else void attempt(async () => {
                const generation = epoch.current;
                const live = () => !sessionIsEnding() && !stopped.current && generation === epoch.current;
                if (!live()) return;
                if (action === 'pause') await helper.request(`/v1/jobs/${job.id}/pause`, {});
                else await reconnectTakenOverJob(job, helper, live);
                if (!live()) return;
                setJobs(await helper.request<IntakeJob[]>('/v1/jobs')); setCloudRefresh(value => value + 1);
              });
            }}>{action === 'continue' ? view.secondary === 'retry' ? c.uploadAgain : c.action : c[action]}</Button>)}</div>
          {(view.problem || view.note) && <p role={view.kind === 'attention' ? 'alert' : 'status'}>{c[codeSentences[message] ?? (Object.hasOwn(c, message) ? message as keyof typeof c : 'error')]}</p>}
          {!!renewed[job.id] && <p role="status">{c.autoRenewed} ({renewed[job.id]})</p>}
          {observation.fetch === 'ok' && observation.at && ['verified','failed'].includes(observation.status?.state ?? '') &&
            <p className="text-sm">{c.checkedAt} {new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false }).format(observation.at)}{' '}
              <button type="button" className="underline" disabled={busy} onClick={() => setCloudRefresh(value => value + 1)}>{c.checkAgain}</button></p>}
        </article>;
      })}</>}
    <details className="space-y-2"><summary>{c.technical}</summary>
      {info && <p>{c.limits}: {info.max_session_bytes.toLocaleString()} {c.bytes} / {info.max_single_file_bytes.toLocaleString()} {c.bytes} / {info.max_files} / {info.reserve_bytes.toLocaleString()}</p>}
      <p>{c.serverDeclaration}</p>
      {pending.map(d => <p key={d.sessionId}>{c.selected}: <code>{d.sessionId}</code> / <code>{d.handoverId}</code> / <code>{d.batchId}</code></p>)}
      {jobs.map(job => { const status = cloud[job.id]?.status;
        return <div key={job.id} className="space-y-2 border-t py-4"><h4>{job.basename}</h4>
          <p>{c.selected}: <code>{job.session_id}</code></p>
          <dl>{Object.entries({ job_id: job.id, handover_id: job.handover_id, batch_id: job.batch_id,
            episode_id: job.record?.episode_id, ingest_id: job.ingest_id, target_batch_id: job.target_batch_id,
            phase: job.phase, reason: job.reason }).map(([key, value]) => <div key={key}><dt>{key}</dt><dd><code>{value ?? '—'}</code></dd></div>)}</dl>
          <p>{job.bytes.toLocaleString()} {c.bytes}</p><p>{c.sentAttempt}: {job.progress_bytes.toLocaleString()} {c.bytes}</p>
          {status && <><p>{label(status.state)} · <code>{status.state}</code> · {status.files_verified} / {status.files_total} {c.files}</p>
            <p><code>{status.reason ?? '—'}</code></p><p>{c.actual}: <code>{status.collection_session_id ?? '—'}</code></p>
            {status.resolution_state === 'quarantined' && <p>{c.attribution}</p>}
            <a className="inline-block underline" target="_blank" rel="noopener noreferrer"
              href={`/pipeline?${new URLSearchParams({ batch_id: status.batch_id, episode_id: status.episode_id, ingest_id: status.ingest_id })}`}>{c.originality}</a></>}
          <p>{c.separate}</p>
        </div>;
      })}
      {lastError && <code>{lastError}</code>}
      {folders.filter(f => f.reason).map(f => <p key={f.id}>{f.basename}: <code>{f.reason}</code></p>)}
    </details>
  </Panel>;
}
